import { useState, useEffect, useMemo, ChangeEvent } from "react";
import moment from "moment";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Loading } from "./Loading";
import { Event } from "../models/Event";

interface SortConfig {
  key: keyof Event;
  order: SortOrder;
}

type SortOrder = "asc" | "desc";

export const Daily = ({ setSelectedView, client, getLocalDate }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const breedFilters = ["All", "Harness", "Thoroughbred"]; //, "Greyhound"];
  const dateFormat = "YYYY-MM-DD";
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedDate, setSelectedDate] = useState(moment().format(dateFormat));
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "EventName",
    order: "asc",
  });
  const [selectedBreedFilter, setSelectedBreedFilter] = useState("All");

  const classNames = (...classes: string[]) => {
    return classes.filter(Boolean).join(" ");
  };

  const onChangeDate = (date: ChangeEvent<HTMLInputElement>) => {
    const newDate = moment(date.target.value).format(dateFormat);
    setSelectedDate(newDate);
  };

  const onChangeBreedFilter = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedBreedFilter(event.target.value);
  };

  const requestSort = (key: keyof Event) => {
    let order = "asc" as SortOrder;
    if (sortConfig.key === key && sortConfig.order === "asc") {
      order = "desc" as SortOrder;
    }
    setSortConfig({ key, order });
  };

  const sortedFilteredEvents = useMemo(() => {
    let sortableItems = [...events];
    if (selectedBreedFilter !== "All") {
      sortableItems = [...events].filter(
        (event) => event.TrackBreedDisplay === selectedBreedFilter
      );
    }
    return sortableItems.sort((a, b) =>
      a[sortConfig.key] > b[sortConfig.key]
        ? sortConfig.order === "asc"
          ? 1
          : -1
        : sortConfig.order === "asc"
        ? -1
        : 1
    );
  }, [events, sortConfig, selectedBreedFilter]);

  useEffect(() => {
    const fetchEvents = async (date: string) => {
      let response = await client.get(`?date=${date}`);
      setEvents(response.data);
      setIsLoading(false);
    };

    setIsLoading(true);
    fetchEvents(selectedDate);
  }, [selectedDate, client]);

  let content = <Loading />;

  if (!isLoading) {
    content = (
      <div>
        <div className="px-2">
          <div className="sm:flex sm:items-center">
            <div className="mt-1 flex">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                Date
              </span>
              <input
                type="date"
                className="border min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 px-3 py-2"
                value={selectedDate}
                onChange={onChangeDate}
              />
            </div>
            <div className="mt-1 flex sm:pl-4">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                Breed
              </span>
              <select
                value={selectedBreedFilter}
                onChange={onChangeBreedFilter}
                className="border min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 px-3 py-2"
              >
                {breedFilters.map((breedFilter) => {
                  return (
                    <option key={breedFilter} value={breedFilter}>
                      {breedFilter}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="mt-1 flex sm:pl-4">
              <button
                className="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-3 text-blue-500 sm:text-sm py-2"
                onClick={() => setSelectedView("weekly")}
              >
                Weekly Schedule
              </button>
            </div>
          </div>
        </div>
        <div className="mt-1 max-h-[600px] overflow-y-auto">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="mt-2 flex flex-col">
              <div className="-my-2 -mx-4 sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle">
                  <div className="shadow-sm ring-1 ring-black ring-opacity-5">
                    {sortedFilteredEvents.length > 0 ? (
                      <table
                        className="min-w-full border-separate"
                        style={{ borderSpacing: 0 }}
                      >
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8"
                            >
                              <button
                                className="group inline-flex"
                                onClick={() => requestSort("PostTimeLocal")}
                              >
                                Post Time
                                <span className="ml-2 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300">
                                  {sortConfig.order === "asc" &&
                                  sortConfig.key === "PostTimeLocal" ? (
                                    <ChevronUpIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <ChevronDownIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  )}
                                </span>
                              </button>
                            </th>
                            <th
                              scope="col"
                              className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                            >
                              <button
                                className="group inline-flex"
                                onClick={() => requestSort("EventName")}
                              >
                                Event Name
                                <span className="ml-2 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300">
                                  {sortConfig.order === "asc" &&
                                  sortConfig.key === "EventName" ? (
                                    <ChevronUpIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <ChevronDownIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  )}
                                </span>
                              </button>
                            </th>
                            <th
                              scope="col"
                              className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                            >
                              Code
                            </th>
                            <th
                              scope="col"
                              className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                            >
                              Breed
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {sortedFilteredEvents.map((event, eventIdx) => {
                            return (
                              <tr key={event.EventCode}>
                                <td
                                  className={classNames(
                                    eventIdx !== sortedFilteredEvents.length - 1
                                      ? "border-b border-gray-200"
                                      : "",
                                    "whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8"
                                  )}
                                >
                                  {getLocalDate(
                                    event.RunDate,
                                    event.PostTimeLocal
                                  )}
                                </td>
                                <td
                                  className={classNames(
                                    eventIdx !== sortedFilteredEvents.length - 1
                                      ? "border-b border-gray-200"
                                      : "",
                                    "whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                  )}
                                >
                                  {event.EventName}
                                </td>
                                <td
                                  className={classNames(
                                    eventIdx !== sortedFilteredEvents.length - 1
                                      ? "border-b border-gray-200"
                                      : "",
                                    "whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                  )}
                                >
                                  <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                                    {event.EventCode}
                                  </span>
                                </td>
                                <td
                                  className={classNames(
                                    eventIdx !== sortedFilteredEvents.length - 1
                                      ? "border-b border-gray-200"
                                      : "",
                                    "whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                  )}
                                >
                                  {event.TrackBreedDisplay}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="rounded-md bg-blue-50 p-4">
                        <div className="flex justify-center">
                          <div className="ml-3 flex">
                            <InformationCircleIcon
                              className="h-5 w-5 text-blue-400 mr-1"
                              aria-hidden="true"
                            />
                            <p className="text-sm text-blue-700">
                              No events for selected date. Please check again
                              later.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <div>{content}</div>;
};
